import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { loadRecordsByEquality } from './firestoreQueryHelpers';
import { isDeliveryBearing } from './lessonHandoff';
import { buildClientCoachingMemory, resolveParticipantVideoOwnerIds } from './clientCoachingMemory';
import {
  createBookingRequest,
  getRequesterBookingRequests,
  getRequesterBookings,
  normalizeBooking,
  sendRebookPrompt,
} from './booking';
import { getRequesterBookingPayments } from './bookingPayments';
import {
  getOrganizationServicePackages,
  getUserActivePackages,
  getUserPackageUsages,
  normalizeCustomerMembership,
  normalizePackageUsage,
} from './servicePackages';
import { createNotification } from './notifications';

function normalizeString(value = '') {
  return String(value || '').trim();
}

function normalizeArray(value = []) {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeString(entry)).filter(Boolean);
  }

  return normalizeString(value)
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function normalizeNumber(value, fallback = 0) {
  if (value === '' || value === null || typeof value === 'undefined') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toDateValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toTimestampValue(value) {
  return toDateValue(value)?.getTime?.() || 0;
}

function buildDisplayName(values = {}) {
  const firstName = normalizeString(values.firstName || '');
  const lastName = normalizeString(values.lastName || '');
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  return (
    normalizeString(values.displayName || '') ||
    fullName ||
    normalizeString(values.name || '') ||
    normalizeString(values.email || '') ||
    'Client'
  );
}

export function normalizeClientProfile(record = {}, fallbackId = '') {
  const id = record.id || record.clientProfileId || fallbackId || '';
  return {
    id,
    clientProfileId: id,
    organizationId: record.organizationId || '',
    // Pass 79 — `clientId` is the CANONICAL commercial identity (may exist with NO Auth account). `userId` is the
    // OPTIONAL explicit Auth link. They are NEVER interchangeable and `userId` is NEVER set to `clientId`.
    clientId: record.clientId || '',
    userId: record.userId || '',
    firstName: normalizeString(record.firstName || ''),
    lastName: normalizeString(record.lastName || ''),
    displayName: buildDisplayName(record),
    email: normalizeString(record.email || ''),
    phone: normalizeString(record.phone || ''),
    country: normalizeString(record.country || ''),
    primaryDiscipline: normalizeString(record.primaryDiscipline || ''),
    skillLevel: normalizeString(record.skillLevel || ''),
    goals: normalizeString(record.goals || ''),
    notesInternal: normalizeString(record.notesInternal || ''),
    tags: normalizeArray(record.tags || []),
    lastLessonAt: record.lastLessonAt || null,
    lastBookingAt: record.lastBookingAt || null,
    lastMessageAt: record.lastMessageAt || null,
    totalBookings: normalizeNumber(record.totalBookings, 0),
    totalLessons: normalizeNumber(record.totalLessons, 0),
    totalVideos: normalizeNumber(record.totalVideos, 0),
    totalRevenue: normalizeNumber(record.totalRevenue, 0),
    retentionLastReviewedAt: record.retentionLastReviewedAt || null,
    retentionLastReviewedByUserId: record.retentionLastReviewedByUserId || '',
    retentionLastActionType: normalizeString(record.retentionLastActionType || ''),
    retentionLastQueueType: normalizeString(record.retentionLastQueueType || ''),
    retentionSnoozedUntil: record.retentionSnoozedUntil || null,
    retentionSnoozedByUserId: record.retentionSnoozedByUserId || '',
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
  };
}

export function normalizeClientNote(record = {}, fallbackId = '') {
  const id = record.id || record.clientNoteId || fallbackId || '';
  return {
    id,
    clientNoteId: id,
    organizationId: record.organizationId || '',
    // Pass 79 — `clientId` is the CANONICAL manual-memory owner (works with NO Auth account). `clientUserId` is an
    // OPTIONAL explicit Auth link only. A note is explicit MANUAL memory — never derived, never a lessonInstance.
    clientId: record.clientId || '',
    clientUserId: record.clientUserId || '',
    instructorId: record.instructorId || '',
    createdByAuthUserId: record.createdByAuthUserId || record.instructorId || '',
    createdByNameSnapshot: normalizeString(record.createdByNameSnapshot || ''),
    noteText: normalizeString(record.noteText || ''),
    tags: normalizeArray(record.tags || []),
    relatedBookingId: record.relatedBookingId || '',
    relatedLessonId: record.relatedLessonId || '',
    visibility: normalizeString(record.visibility || 'org'),
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
  };
}

// Pass 79 — resolve a client profile doc by the CANONICAL commercial `clientId` first (a no-Auth client only has
// this), falling back to the legacy Auth `userId`. Never mixes the two identities into one key.
async function getClientProfileDocRefs(db, { organizationId = '', clientId = '', userId = '' } = {}) {
  if (!organizationId) return [];
  if (clientId) {
    const snap = await getDocs(
      query(
        collection(db, PLATFORM_COLLECTIONS.CLIENT_PROFILES),
        where('organizationId', '==', organizationId),
        where('clientId', '==', clientId)
      )
    );
    if ((snap.docs || []).length > 0) return snap.docs;
  }
  if (userId) {
    const snap = await getDocs(
      query(
        collection(db, PLATFORM_COLLECTIONS.CLIENT_PROFILES),
        where('organizationId', '==', organizationId),
        where('userId', '==', userId)
      )
    );
    return snap.docs || [];
  }
  return [];
}

async function loadUserRecord(db, userId = '') {
  if (!userId) return null;
  const snapshot = await getDoc(doc(db, 'users', userId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

export async function upsertClientProfile(
  db,
  {
    organizationId = '',
    clientId = '',
    userId = '',
    profileData = {},
  } = {}
) {
  // Pass 79 — a profile is addressable by the canonical `clientId` OR a legacy Auth `userId` (never both fabricated).
  if (!organizationId || (!clientId && !userId)) {
    throw new Error('organizationId and one of clientId/userId are required.');
  }

  const normalized = normalizeClientProfile({
    organizationId,
    clientId,
    userId,
    ...profileData,
    // identity fields are authoritative from the call args — never let profileData move them
    ...(clientId ? { clientId } : {}),
    ...(userId ? { userId } : {}),
  });
  const existingDocs = await getClientProfileDocRefs(db, { organizationId, clientId, userId });

  if (existingDocs.length > 0) {
    const [existingDoc] = existingDocs;
    await updateDoc(doc(db, PLATFORM_COLLECTIONS.CLIENT_PROFILES, existingDoc.id), {
      ...normalized,
      updatedAt: serverTimestamp(),
    });
    return normalizeClientProfile(normalized, existingDoc.id);
  }

  const docRef = await addDoc(collection(db, PLATFORM_COLLECTIONS.CLIENT_PROFILES), {
    ...normalized,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return normalizeClientProfile(normalized, docRef.id);
}

export async function updateClientProfile(
  db,
  {
    organizationId = '',
    clientId = '',
    userId = '',
    updates = {},
  } = {}
) {
  if (!organizationId || (!clientId && !userId)) {
    throw new Error('organizationId and one of clientId/userId are required.');
  }

  const existingProfile = await getClientProfile(db, { organizationId, clientId, userId });
  const nextProfile = normalizeClientProfile({
    ...(existingProfile || {}),
    organizationId,
    // preserve the existing identity linkage; never overwrite one identity with the other
    clientId: clientId || existingProfile?.clientId || '',
    userId: userId || existingProfile?.userId || '',
    ...updates,
  }, existingProfile?.clientProfileId || '');

  return upsertClientProfile(db, {
    organizationId,
    clientId: nextProfile.clientId,
    userId: nextProfile.userId,
    profileData: nextProfile,
  });
}

export async function updateClientGoals(db, { organizationId = '', userId = '', goals = '' } = {}) {
  return updateClientProfile(db, {
    organizationId,
    userId,
    updates: { goals },
  });
}

export async function updateClientTags(db, { organizationId = '', userId = '', tags = [] } = {}) {
  return updateClientProfile(db, {
    organizationId,
    userId,
    updates: { tags },
  });
}

export async function updateClientSummary(db, { organizationId = '', userId = '', notesInternal = '' } = {}) {
  return updateClientProfile(db, {
    organizationId,
    userId,
    updates: { notesInternal },
  });
}

export async function getClientProfile(db, { organizationId = '', clientId = '', userId = '' } = {}) {
  const docs = await getClientProfileDocRefs(db, { organizationId, clientId, userId });
  if (docs.length === 0) return null;
  const [clientProfileDoc] = docs;
  return normalizeClientProfile({ id: clientProfileDoc.id, ...clientProfileDoc.data() });
}

export async function getOrganizationClientProfiles(db, organizationId = '') {
  if (!organizationId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.CLIENT_PROFILES,
    field: 'organizationId',
    value: organizationId,
  });
  return records.map((record) => normalizeClientProfile(record));
}

// Pass 80 §5/§6 — org-wide delivered-lesson evidence for the retention read model. Candidacy is derived from
// canonical commercial records (bookings/lessons/requests), NOT from a persisted clientProfile, so a no-Auth
// client with only a delivered lessonInstance still surfaces. Tenant-scoped; delivery-bearing filtering is the
// caller's concern (retentionOpportunity applies isDeliveryBearing).
export async function getOrganizationLessons(db, organizationId = '') {
  if (!organizationId) return [];
  return loadRecordsByEquality(db, {
    collectionName: 'lessonInstances',
    field: 'organizationId',
    value: organizationId,
  });
}

export async function getClientProfilesForUser(db, userId = '') {
  if (!userId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.CLIENT_PROFILES,
    field: 'userId',
    value: userId,
  });
  return records.map((record) => normalizeClientProfile(record));
}

// Pass 79 — a manual client note is clientId-NATIVE. It requires the canonical `clientId` and the authoring staff's
// Auth uid (`createdByAuthUserId`); `clientUserId` is an OPTIONAL explicit Auth link only. `clientId` is NEVER used as
// `clientUserId`. Identity/lineage is immutable once written (enforced in Rules).
export async function createClientNote(
  db,
  {
    organizationId = '',
    clientId = '',
    clientUserId = '',
    createdByAuthUserId = '',
    createdByNameSnapshot = '',
    instructorId = '',
    noteText = '',
    tags = [],
    relatedBookingId = '',
    relatedLessonId = '',
    visibility = 'org',
  } = {}
) {
  const author = createdByAuthUserId || instructorId;
  if (!organizationId || !clientId || !author || !normalizeString(noteText)) {
    throw new Error('organizationId, clientId, createdByAuthUserId, and noteText are required.');
  }

  const normalized = normalizeClientNote({
    organizationId,
    clientId,
    clientUserId,
    createdByAuthUserId: author,
    createdByNameSnapshot,
    instructorId: author,
    noteText,
    tags,
    relatedBookingId,
    relatedLessonId,
    visibility,
  });

  const docRef = await addDoc(collection(db, PLATFORM_COLLECTIONS.CLIENT_NOTES), {
    ...normalized,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return normalizeClientNote(normalized, docRef.id);
}

// Read the canonical clientId-native notes MERGED with legacy clientUserId notes (only when an explicit Auth link
// exists). Deduped by note document id, org-scoped. Never fuzzy-matched.
export async function getClientNotes(
  db,
  {
    organizationId = '',
    clientId = '',
    clientUserId = '',
  } = {}
) {
  if (!organizationId || (!clientId && !clientUserId)) return [];
  const [modern, legacy] = await Promise.all([
    clientId ? loadRecordsByEquality(db, { collectionName: PLATFORM_COLLECTIONS.CLIENT_NOTES, field: 'clientId', value: clientId }) : Promise.resolve([]),
    clientUserId ? loadRecordsByEquality(db, { collectionName: PLATFORM_COLLECTIONS.CLIENT_NOTES, field: 'clientUserId', value: clientUserId }) : Promise.resolve([]),
  ]);
  const seen = new Set();
  return [...modern, ...legacy]
    .map((record) => normalizeClientNote(record))
    .filter((note) => {
      if (note.organizationId !== organizationId) return false;
      const id = note.clientNoteId || note.id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .sort((left, right) => toTimestampValue(right.updatedAt || right.createdAt) - toTimestampValue(left.updatedAt || left.createdAt));
}

// Pass 70A — commercial booking/request history. PRIMARY link is the canonical Pass-63 `clientId` (present
// for a no-Auth commercial customer); LEGACY link is `requesterUserId == clientUserId` for Auth-backed clients.
// Both are queried and MERGED, deduped by canonical document id, org-scoped. Never fuzzy-matched; requesterUserId
// is never turned into a clientId.
const dedupeById = (records = []) => {
  const seen = new Set();
  return records.filter((r) => { const id = r.id || r.bookingRequestId || r.bookingId || ''; if (!id || seen.has(id)) return false; seen.add(id); return true; });
};
export async function getClientBookingHistory(
  db,
  {
    organizationId = '',
    clientUserId = '',
    clientId = '',
  } = {}
) {
  const [legacyReqs, legacyBks, modernReqs, modernBks] = await Promise.all([
    clientUserId ? getRequesterBookingRequests(db, clientUserId) : Promise.resolve([]),
    clientUserId ? getRequesterBookings(db, clientUserId) : Promise.resolve([]),
    clientId ? loadRecordsByEquality(db, { collectionName: 'bookingRequests', field: 'clientId', value: clientId }) : Promise.resolve([]),
    clientId ? loadRecordsByEquality(db, { collectionName: 'bookings', field: 'clientId', value: clientId }) : Promise.resolve([]),
  ]);
  const inOrg = (r) => !organizationId || (r.organizationId || r.orgId || '') === organizationId;
  return {
    bookingRequests: dedupeById([...(legacyReqs || []), ...(modernReqs || [])]).filter(inOrg),
    bookings: dedupeById([...(legacyBks || []), ...(modernBks || [])]).filter(inOrg),
  };
}

// Pass 69 — the modern commercial-client lesson history. A lessonInstance belongs to a client via the
// canonical Pass-63 `clientId` (which may exist with NO Auth participant), OR via the legacy Auth identity
// (`studentId`/`clientUserId`) for older coaching records. Both are queried and MERGED, deduped by lesson
// document id. NEVER fuzzy-matched by name/date. Org-scoped. No student identity is ever fabricated.
export async function getClientLessonHistory(
  db,
  {
    organizationId = '',
    clientUserId = '',
    clientId = '',
  } = {}
) {
  if (!clientUserId && !clientId) return [];
  const queries = [];
  // MODERN: the commercial clientId is the primary link.
  if (clientId) queries.push(loadRecordsByEquality(db, { collectionName: 'lessonInstances', field: 'clientId', value: clientId }));
  // LEGACY: only when the client has an explicit Auth identity (studentId == that uid).
  if (clientUserId) queries.push(loadRecordsByEquality(db, { collectionName: 'lessonInstances', field: 'studentId', value: clientUserId }));
  const groups = await Promise.all(queries);
  const seen = new Set();
  return groups.flat()
    .filter((lesson) => {
      const id = lesson.id || lesson.lessonId || '';
      if (!id || seen.has(id)) return false;              // §37 dedupe by canonical lesson document id
      seen.add(id);
      return !organizationId || (lesson.organizationId || lesson.orgId || '') === organizationId; // §12 tenant
    })
    .sort((left, right) => lessonHistoryTime(right) - lessonHistoryTime(left));
}

// §18/§19 deterministic event-time precedence: explicit delivery/completion time → scheduledAt → date →
// createdAt. NOT updatedAt (a late edit must not jump an old lesson to "today").
export function lessonHistoryTime(lesson = {}) {
  return toTimestampValue(lesson.completedAt || lesson.deliveredAt || lesson.scheduledAt || lesson.date || lesson.serviceDate || lesson.createdAt);
}

// §58 pure, source-neutral client-history item for a delivered lesson. No Firestore, no PII invention. The
// lessonInstanceId + bookingId are retained for dedupe/navigation; a draft (not delivery-bearing) is marked
// `delivered:false` so a surface can omit it or show it as planned — it is never counted as delivered.
export function buildLessonHistoryItem(lesson = {}) {
  const id = lesson.id || lesson.lessonId || '';
  const outcome = lesson.lessonOutcome || {};
  const focus = Array.isArray(lesson.stages)
    ? [...new Set(lesson.stages.filter((s) => s && (s.completionStatus === 'completed' || s.completionStatus === 'attempted')).map((s) => String(s.stage || '').trim()).filter(Boolean))]
    : [];
  const notes = String(outcome.instructorSummary || '').trim()
    || (Array.isArray(lesson.stages) ? String((lesson.stages.find((s) => s && String(s.instructorNotes || '').trim()) || {}).instructorNotes || '').trim() : '');
  const delivered = isDeliveryBearing(lesson);
  return {
    id: `lesson:${id}`,
    type: 'lesson',
    sourceType: 'lesson',
    sourceId: id,                                  // §59 internal provenance — not shown prominently
    lessonInstanceId: id,
    bookingId: String(lesson.bookingId || ''),     // §30 traceback (only when booking-linked)
    at: lessonHistoryTime(lesson),
    delivered,                                     // §20/§52 delivery-bearing — draft is never "delivered"
    title: delivered ? 'Lesson delivered' : 'Lesson (draft)',
    sport: String(lesson.sport || '').trim(),
    instructorName: String(lesson.instructorNameSnapshot || '').trim(),
    outcome: String(outcome.status || '').trim(),  // §22/§23 raw human-entered outcome (not an opaque score)
    skillConfidence: String(outcome.skillConfidence || '').trim(),
    targetSkillId: String(lesson.targetSkillId || '').trim(),
    focus,
    notes,                                         // §21 LESSON delivery note (distinct from a manual clientNote)
    // §5/§33 — the customer is the commercial client; participant identity is NOT asserted here.
    clientId: String(lesson.clientId || ''),
  };
}

export async function getClientVideoHistory(
  db,
  {
    organizationId = '',
    clientUserId = '',
  } = {}
) {
  if (!clientUserId) return [];
  const [userRecords, studentRecords] = await Promise.all([
    loadRecordsByEquality(db, {
      collectionName: 'videos',
      field: 'userId',
      value: clientUserId,
    }),
    loadRecordsByEquality(db, {
      collectionName: 'videos',
      field: 'studentId',
      value: clientUserId,
    }),
  ]);

  const seen = new Set();
  return [...userRecords, ...studentRecords]
    .filter((record) => {
      const videoId = record.id || '';
      if (!videoId || seen.has(videoId)) return false;
      seen.add(videoId);
      return !organizationId || (record.organizationId || record.orgId || '') === organizationId;
    })
    .sort((left, right) => toTimestampValue(right.updatedAt || right.createdAt) - toTimestampValue(left.updatedAt || left.createdAt));
}

// Pass 79 §8 — reviewed video evidence for the EXPLICIT participants of a client (owner uid OR assigned studentId ==
// an explicit participant/account id). A commercial purchaser is NEVER assumed to be the coached participant; only
// the passed-in owner ids (the account holder + each lesson's explicit studentId) are queried. Org-scoped, deduped.
export async function getClientVideosForOwners(db, { organizationId = '', ownerIds = [] } = {}) {
  const ids = [...new Set((ownerIds || []).map((id) => String(id || '').trim()).filter(Boolean))];
  if (ids.length === 0) return [];
  const groups = await Promise.all(
    ids.flatMap((ownerId) => [
      loadRecordsByEquality(db, { collectionName: 'videos', field: 'userId', value: ownerId }),
      loadRecordsByEquality(db, { collectionName: 'videos', field: 'studentId', value: ownerId }),
    ])
  );
  const seen = new Set();
  return groups.flat()
    .filter((record) => {
      const videoId = record.id || '';
      if (!videoId || seen.has(videoId)) return false;
      seen.add(videoId);
      return !organizationId || (record.organizationId || record.orgId || '') === organizationId;
    })
    .sort((left, right) => toTimestampValue(right.reviewedAt || right.updatedAt || right.createdAt) - toTimestampValue(left.reviewedAt || left.updatedAt || left.createdAt));
}

export function getClientFeedbackHistory(videos = [], bookings = []) {
  const bookingMap = (bookings || []).reduce((acc, booking) => {
    const normalizedBooking = normalizeBooking(booking);
    acc[normalizedBooking.videoId] = normalizedBooking;
    return acc;
  }, {});

  return (videos || [])
    .filter((video) => video?.analysis || video?.feedback || video?.review || bookingMap[video.id || '']?.feedbackId || bookingMap[video.id || '']?.aiReviewId)
    .map((video) => ({
      id: video.id || '',
      videoId: video.id || '',
      title: normalizeString(video.title || video.fileName || 'Session review'),
      createdAt: video.updatedAt || video.createdAt || null,
      hasAnalysis: Boolean(video.analysis),
      feedbackId: bookingMap[video.id || '']?.feedbackId || '',
      aiReviewId: bookingMap[video.id || '']?.aiReviewId || '',
    }))
    .sort((left, right) => toTimestampValue(right.createdAt) - toTimestampValue(left.createdAt));
}

export async function getClientPackageSummary(
  db,
  {
    organizationId = '',
    clientUserId = '',
  } = {}
) {
  const [memberships, usages, servicePackages] = await Promise.all([
    getUserActivePackages(db, clientUserId, { organizationId }),
    getUserPackageUsages(db, clientUserId),
    getOrganizationServicePackages(db, organizationId),
  ]);

  const filteredUsages = (usages || [])
    .map((usage) => normalizePackageUsage(usage))
    .filter((usage) => !organizationId || usage.organizationId === organizationId);
  const activeMemberships = (memberships || []).map((membership) => normalizeCustomerMembership(membership));

  return {
    activeMemberships,
    packageUsages: filteredUsages,
    servicePackages,
    remainingSessions: activeMemberships.reduce((sum, membership) => sum + (membership.remainingSessions || 0), 0),
    remainingCredits: activeMemberships.reduce((sum, membership) => sum + (membership.remainingCredits || 0), 0),
  };
}

export function getClientRevenueSummary(bookings = [], payments = []) {
  const normalizedBookings = (bookings || []).map((booking) => normalizeBooking(booking));
  const relatedPayments = (payments || []).filter((payment) => {
    const bookingId = payment.bookingId || '';
    return normalizedBookings.some((booking) => booking.bookingId === bookingId);
  });

  const settledStatuses = new Set(['authorized', 'paid', 'partially_paid', 'waived']);
  const totalRevenue = relatedPayments
    .filter((payment) => settledStatuses.has(payment.status))
    .reduce((sum, payment) => sum + normalizeNumber(payment.amount, 0), 0);

  return {
    totalRevenue: Number(totalRevenue.toFixed(2)),
    paidBookingCount: new Set(relatedPayments.filter((payment) => settledStatuses.has(payment.status)).map((payment) => payment.bookingId)).size,
    paymentCount: relatedPayments.length,
  };
}

export function getClientLastActivity(timeline = []) {
  return (timeline || [])
    .slice()
    .sort((left, right) => toTimestampValue(right.at) - toTimestampValue(left.at))[0] || null;
}

async function getClientThreadHistory(
  db,
  {
    clientUserId = '',
  } = {}
) {
  if (!clientUserId) return [];

  const snapshot = await getDocs(
    query(collection(db, 'threads'), where('participantIds', 'array-contains', clientUserId))
  );

  return (snapshot.docs || [])
    .map((threadDoc) => ({ id: threadDoc.id, ...threadDoc.data() }))
    .sort((left, right) => toTimestampValue(right.lastMessageTimestamp) - toTimestampValue(left.lastMessageTimestamp));
}

export async function getClientTimeline(
  db,
  {
    organizationId = '',
    clientUserId = '',
    clientId = '',
    bookingHistory = null,
    lessons = null,
    videos = null,
    notes = null,
    packageSummary = null,
  } = {}
) {
  if (!clientUserId && !clientId) return [];

  const [
    resolvedBookingHistory,
    resolvedLessons,
    resolvedVideos,
    resolvedNotes,
    resolvedPackageSummary,
    threadHistory,
  ] = await Promise.all([
    bookingHistory || getClientBookingHistory(db, { organizationId, clientUserId, clientId }),
    lessons || getClientLessonHistory(db, { organizationId, clientUserId, clientId }),
    videos || getClientVideoHistory(db, { organizationId, clientUserId }),
    notes || getClientNotes(db, { organizationId, clientUserId }),
    packageSummary || getClientPackageSummary(db, { organizationId, clientUserId }),
    clientUserId ? getClientThreadHistory(db, { clientUserId }) : Promise.resolve([]),
  ]);

  const feedbackHistory = getClientFeedbackHistory(
    resolvedVideos,
    resolvedBookingHistory?.bookings || []
  );

  const timeline = [
    ...(resolvedBookingHistory?.bookings || []).map((booking) => ({
      id: `booking:${booking.bookingId || booking.id}`,
      type: 'booking',
      title: booking.serviceNameSnapshot || booking.serviceType || 'Booking confirmed',
      summary: booking.status || 'confirmed',
      at: booking.startAt || booking.updatedAt || booking.createdAt,
      link: '/my-bookings',
    })),
    ...(resolvedBookingHistory?.bookingRequests || []).map((bookingRequest) => ({
      id: `booking_request:${bookingRequest.bookingRequestId || bookingRequest.id}`,
      type: 'booking_request',
      title: bookingRequest.serviceNameSnapshot || bookingRequest.serviceType || 'Booking request',
      summary: bookingRequest.status || 'pending',
      at: bookingRequest.updatedAt || bookingRequest.createdAt,
      link: '/my-bookings',
    })),
    // Pass 69 — a delivered lesson is a DERIVED history item from the canonical lessonInstance (Pass 68
    // hardened it). Enriched with outcome/focus/notes + delivery-bearing; the note here is the LESSON
    // delivery note, kept distinct from a manual clientNote (separate `note` event below).
    ...(resolvedLessons || []).map((lesson) => {
      const item = buildLessonHistoryItem(lesson);
      return {
        id: item.id, type: 'lesson', sourceId: item.sourceId, lessonInstanceId: item.lessonInstanceId,
        bookingId: item.bookingId, delivered: item.delivered,
        title: item.title, summary: item.outcome || (item.delivered ? 'Delivered' : 'Draft'),
        sport: item.sport, instructorName: item.instructorName, focus: item.focus, notes: item.notes,
        at: item.at, link: item.lessonInstanceId ? `/instructor/lesson-builder?lessonId=${item.lessonInstanceId}` : '/lessons',
      };
    }),
    ...(resolvedVideos || []).map((video) => ({
      id: `video:${video.id}`,
      type: 'video',
      title: video.title || video.fileName || 'Video upload',
      summary: video.status || 'uploaded',
      at: video.updatedAt || video.createdAt,
      link: video.id ? `/reviews/${video.id}` : '/upload',
    })),
    ...(feedbackHistory || []).map((feedbackItem) => ({
      id: `feedback:${feedbackItem.videoId}`,
      type: 'feedback',
      title: feedbackItem.title,
      summary: feedbackItem.hasAnalysis ? 'Movement analysis and coaching ready' : 'Coach feedback ready',
      at: feedbackItem.createdAt,
      link: feedbackItem.videoId ? `/reviews/${feedbackItem.videoId}` : '/me',
    })),
    ...(resolvedNotes || []).map((note) => ({
      id: `note:${note.clientNoteId || note.id}`,
      type: 'note',
      title: 'Internal client note',
      summary: note.noteText,
      at: note.updatedAt || note.createdAt,
      link: '',
    })),
    ...(resolvedPackageSummary?.packageUsages || []).map((usage) => ({
      id: `package_usage:${usage.packageUsageId || usage.id}`,
      type: 'package_usage',
      title: 'Package coverage used',
      summary: usage.amountCovered > 0 ? `${usage.amountCovered} covered` : 'Session credit applied',
      at: usage.createdAt,
      link: '/my-bookings',
    })),
    ...(threadHistory || []).map((thread) => ({
      id: `thread:${thread.id}`,
      type: 'message',
      title: thread.title || 'Message thread',
      summary: thread.lastMessage || 'Conversation updated',
      at: thread.lastMessageTimestamp,
      link: `/inbox?tab=messages&thread=${thread.id}`,
    })),
  ];

  return timeline
    .filter((entry) => entry.at)
    .sort((left, right) => toTimestampValue(right.at) - toTimestampValue(left.at));
}

export function getClientsNeedingRebook(
  clientProfiles = [],
  {
    bookings = [],
    daysSince = 21,
    now = new Date(),
  } = {}
) {
  const cutoff = now.getTime() - (daysSince * 24 * 60 * 60 * 1000);
  const bookingsByUserId = (bookings || []).reduce((acc, booking) => {
    const normalizedBooking = normalizeBooking(booking);
    const userId = normalizedBooking.requesterUserId || '';
    if (!userId) return acc;
    if (!acc[userId]) acc[userId] = [];
    acc[userId].push(normalizedBooking);
    return acc;
  }, {});

  return (clientProfiles || [])
    .map((profile) => normalizeClientProfile(profile))
    .filter((profile) => {
      const clientBookings = bookingsByUserId[profile.userId] || [];
      const hasUpcoming = clientBookings.some((booking) => ['confirmed', 'upcoming', 'in_progress'].includes(booking.status));
      if (hasUpcoming) return false;
      const lastCompleted = clientBookings
        .filter((booking) => booking.status === 'completed')
        .sort((left, right) => toTimestampValue(right.endAt || right.updatedAt) - toTimestampValue(left.endAt || left.updatedAt))[0];
      return lastCompleted && toTimestampValue(lastCompleted.endAt || lastCompleted.updatedAt) <= cutoff;
    });
}

export function getClientsWithUnusedCredits(memberships = []) {
  return (memberships || [])
    .map((membership) => normalizeCustomerMembership(membership))
    .filter((membership) => membership.status === 'active')
    .filter((membership) => (membership.remainingSessions || 0) > 0 || (membership.remainingCredits || 0) > 0);
}

export function getClientsWithExpiringMemberships(memberships = [], { withinDays = 14, now = new Date() } = {}) {
  const threshold = now.getTime() + (withinDays * 24 * 60 * 60 * 1000);
  return (memberships || [])
    .map((membership) => normalizeCustomerMembership(membership))
    .filter((membership) => membership.status === 'active')
    .filter((membership) => {
      const endTime = toTimestampValue(membership.endAt);
      return endTime > 0 && endTime <= threshold;
    });
}

export function getClientsWithFeedbackReadyNoBooking(
  clientProfiles = [],
  {
    bookings = [],
  } = {}
) {
  const bookingsByUserId = (bookings || []).reduce((acc, booking) => {
    const normalizedBooking = normalizeBooking(booking);
    const userId = normalizedBooking.requesterUserId || '';
    if (!userId) return acc;
    if (!acc[userId]) acc[userId] = [];
    acc[userId].push(normalizedBooking);
    return acc;
  }, {});

  return (clientProfiles || [])
    .map((profile) => normalizeClientProfile(profile))
    .filter((profile) => {
      const clientBookings = bookingsByUserId[profile.userId] || [];
      const hasFeedbackReady = clientBookings.some((booking) => booking.feedbackId || booking.aiReviewId || booking.videoId);
      const hasUpcoming = clientBookings.some((booking) => ['confirmed', 'upcoming', 'in_progress'].includes(booking.status));
      return hasFeedbackReady && !hasUpcoming;
    });
}

export function getClientsWithFollowUpRecommended(
  clientProfiles = [],
  {
    bookings = [],
  } = {}
) {
  const bookingsByUserId = (bookings || []).reduce((acc, booking) => {
    const normalizedBooking = normalizeBooking(booking);
    const userId = normalizedBooking.requesterUserId || '';
    if (!userId) return acc;
    if (!acc[userId]) acc[userId] = [];
    acc[userId].push(normalizedBooking);
    return acc;
  }, {});

  return (clientProfiles || [])
    .map((profile) => normalizeClientProfile(profile))
    .filter((profile) => (bookingsByUserId[profile.userId] || []).some((booking) => Boolean(booking.followUpRecommended)));
}

export function getClientsWithPackageBalanceNoUpcomingSession(
  clientProfiles = [],
  {
    bookings = [],
    memberships = [],
  } = {}
) {
  const bookingsByUserId = (bookings || []).reduce((acc, booking) => {
    const normalizedBooking = normalizeBooking(booking);
    const userId = normalizedBooking.requesterUserId || '';
    if (!userId) return acc;
    if (!acc[userId]) acc[userId] = [];
    acc[userId].push(normalizedBooking);
    return acc;
  }, {});

  const activeCoverageByUserId = (memberships || [])
    .map((membership) => normalizeCustomerMembership(membership))
    .filter((membership) => membership.status === 'active')
    .reduce((acc, membership) => {
      if (!acc[membership.userId]) acc[membership.userId] = [];
      acc[membership.userId].push(membership);
      return acc;
    }, {});

  return (clientProfiles || [])
    .map((profile) => normalizeClientProfile(profile))
    .filter((profile) => {
      const clientMemberships = activeCoverageByUserId[profile.userId] || [];
      if (clientMemberships.length === 0) return false;
      const hasBalance = clientMemberships.some((membership) => (membership.remainingSessions || 0) > 0 || (membership.remainingCredits || 0) > 0);
      const hasUpcoming = (bookingsByUserId[profile.userId] || []).some((booking) => ['confirmed', 'upcoming', 'in_progress'].includes(booking.status));
      return hasBalance && !hasUpcoming;
    });
}

export function getInactiveClients(clientProfiles = [], { timelineItemsByUserId = {}, daysSince = 45, now = new Date() } = {}) {
  const cutoff = now.getTime() - (daysSince * 24 * 60 * 60 * 1000);
  return (clientProfiles || [])
    .map((profile) => normalizeClientProfile(profile))
    .filter((profile) => {
      const signals = getClientInactivitySignals(profile, {
        timeline: timelineItemsByUserId[profile.userId] || [],
        now,
      });
      return !signals.hasUpcomingBooking && signals.daysSinceLastActivity >= Math.floor((now.getTime() - cutoff) / (24 * 60 * 60 * 1000));
    });
}

export function getClientInactivitySignals(
  clientProfile = {},
  {
    bookings = [],
    lessons = [],
    feedbackHistory = [],
    timeline = [],
    memberships = [],
    now = new Date(),
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const normalizedBookings = (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .filter((booking) => !normalizedProfile.userId || booking.requesterUserId === normalizedProfile.userId);
  const relevantLessons = (lessons || [])
    .filter((lesson) => {
      const studentId = lesson.studentId || lesson.userId || lesson.clientUserId || '';
      return !normalizedProfile.userId || studentId === normalizedProfile.userId;
    });
  const relevantMemberships = (memberships || [])
    .map((membership) => normalizeCustomerMembership(membership))
    .filter((membership) => !normalizedProfile.userId || membership.userId === normalizedProfile.userId);
  const relevantFeedback = (feedbackHistory || []);
  const relevantTimeline = (timeline || []);
  const lastTimelineBooking = relevantTimeline
    .filter((entry) => entry.type === 'booking' || entry.type === 'booking_request')
    .slice()
    .sort((left, right) => toTimestampValue(right.at) - toTimestampValue(left.at))[0] || null;
  const lastTimelineLesson = relevantTimeline
    .filter((entry) => entry.type === 'lesson')
    .slice()
    .sort((left, right) => toTimestampValue(right.at) - toTimestampValue(left.at))[0] || null;
  const lastTimelineFeedback = relevantTimeline
    .filter((entry) => entry.type === 'feedback')
    .slice()
    .sort((left, right) => toTimestampValue(right.at) - toTimestampValue(left.at))[0] || null;

  const lastCompletedBooking = normalizedBookings
    .filter((booking) => booking.status === 'completed')
    .sort((left, right) => toTimestampValue(right.endAt || right.updatedAt || right.startAt) - toTimestampValue(left.endAt || left.updatedAt || left.startAt))[0]
    || (lastTimelineBooking ? { endAt: lastTimelineBooking.at } : null);
  const lastLesson = relevantLessons
    .slice()
    .sort((left, right) => toTimestampValue(right.updatedAt || right.date || right.createdAt) - toTimestampValue(left.updatedAt || left.date || left.createdAt))[0]
    || (lastTimelineLesson ? { updatedAt: lastTimelineLesson.at } : null);
  const lastFeedback = relevantFeedback
    .slice()
    .sort((left, right) => toTimestampValue(right.createdAt) - toTimestampValue(left.createdAt))[0]
    || (lastTimelineFeedback ? { createdAt: lastTimelineFeedback.at } : null);
  const lastMessage = relevantTimeline
    .filter((entry) => entry.type === 'message')
    .slice()
    .sort((left, right) => toTimestampValue(right.at) - toTimestampValue(left.at))[0] || null;
  const lastActivity = getClientLastActivity([
    ...(lastCompletedBooking ? [{ at: lastCompletedBooking.endAt || lastCompletedBooking.updatedAt || lastCompletedBooking.startAt }] : []),
    ...(lastLesson ? [{ at: lastLesson.updatedAt || lastLesson.date || lastLesson.createdAt }] : []),
    ...(lastFeedback ? [{ at: lastFeedback.createdAt }] : []),
    ...(lastMessage ? [{ at: lastMessage.at }] : []),
    ...(normalizedProfile.lastBookingAt ? [{ at: normalizedProfile.lastBookingAt }] : []),
    ...(normalizedProfile.lastLessonAt ? [{ at: normalizedProfile.lastLessonAt }] : []),
    ...(normalizedProfile.lastMessageAt ? [{ at: normalizedProfile.lastMessageAt }] : []),
  ]);
  const lastActivityAt = lastActivity?.at || null;
  const daysSinceLastActivity = lastActivityAt
    ? Math.max(0, Math.floor((now.getTime() - toTimestampValue(lastActivityAt)) / (24 * 60 * 60 * 1000)))
    : 999;
  const upcomingBookings = normalizedBookings.filter((booking) => ['confirmed', 'upcoming', 'in_progress'].includes(booking.status));
  const completedBookings = normalizedBookings
    .filter((booking) => booking.status === 'completed')
    .sort((left, right) => toTimestampValue(right.endAt || right.updatedAt || right.startAt) - toTimestampValue(left.endAt || left.updatedAt || left.startAt));
  let averageCadenceDays = null;
  if (completedBookings.length >= 2) {
    const intervals = completedBookings.slice(0, -1).map((booking, index) => {
      const currentTime = toTimestampValue(booking.endAt || booking.updatedAt || booking.startAt);
      const nextTime = toTimestampValue(completedBookings[index + 1].endAt || completedBookings[index + 1].updatedAt || completedBookings[index + 1].startAt);
      return Math.max(0, currentTime - nextTime);
    });
    const averageInterval = intervals.reduce((sum, interval) => sum + interval, 0) / Math.max(intervals.length, 1);
    averageCadenceDays = Math.round(averageInterval / (24 * 60 * 60 * 1000));
  }

  return {
    clientUserId: normalizedProfile.userId,
    lastCompletedBookingAt: lastCompletedBooking?.endAt || lastCompletedBooking?.updatedAt || lastCompletedBooking?.startAt || normalizedProfile.lastBookingAt || null,
    lastLessonAt: lastLesson?.updatedAt || lastLesson?.date || lastLesson?.createdAt || normalizedProfile.lastLessonAt || null,
    lastFeedbackAt: lastFeedback?.createdAt || null,
    lastMessageAt: lastMessage?.at || normalizedProfile.lastMessageAt || null,
    lastActivityAt,
    daysSinceLastActivity,
    hasUpcomingBooking: upcomingBookings.length > 0,
    remainingSessions: relevantMemberships.reduce((sum, membership) => sum + (membership.remainingSessions || 0), 0),
    remainingCredits: relevantMemberships.reduce((sum, membership) => sum + (membership.remainingCredits || 0), 0),
    averageCadenceDays,
    totalCompletedBookings: completedBookings.length,
    lastActivityType: lastMessage
      ? 'message'
      : lastFeedback
        ? 'feedback'
        : lastLesson
          ? 'lesson'
          : lastCompletedBooking
            ? 'booking'
            : 'none',
  };
}

export function scoreClientRetentionRisk(signals = {}) {
  let score = 0;
  const reasons = [];

  if (!signals.hasUpcomingBooking && (signals.daysSinceLastActivity || 0) >= 45) {
    score += 3;
    reasons.push('No upcoming session and activity has been quiet for 45+ days.');
  } else if (!signals.hasUpcomingBooking && (signals.daysSinceLastActivity || 0) >= 21) {
    score += 2;
    reasons.push('No upcoming session and recent activity is tapering off.');
  }

  if (!signals.hasUpcomingBooking && ((signals.remainingSessions || 0) > 0 || (signals.remainingCredits || 0) > 0)) {
    score += 2;
    reasons.push('Unused prepaid value is still available without a future booking.');
  }

  if (signals.averageCadenceDays && (signals.daysSinceLastActivity || 0) > signals.averageCadenceDays + 7) {
    score += 1;
    reasons.push('The client is past their recent coaching cadence.');
  }

  if ((signals.daysSinceLastActivity || 0) >= 60) {
    score += 1;
    reasons.push('Activity has been dormant for an extended stretch.');
  }

  const level = score >= 5 ? 'high' : score >= 3 ? 'medium' : 'low';

  return {
    score,
    level,
    reasons,
  };
}

export function getClientsAtRiskOfDropoff(
  clientProfiles = [],
  {
    bookingsByUserId = {},
    lessonsByUserId = {},
    feedbackHistoryByUserId = {},
    timelineItemsByUserId = {},
    membershipsByUserId = {},
    now = new Date(),
  } = {}
) {
  return (clientProfiles || [])
    .map((profile) => normalizeClientProfile(profile))
    .map((profile) => {
      const signals = getClientInactivitySignals(profile, {
        bookings: bookingsByUserId[profile.userId] || [],
        lessons: lessonsByUserId[profile.userId] || [],
        feedbackHistory: feedbackHistoryByUserId[profile.userId] || [],
        timeline: timelineItemsByUserId[profile.userId] || [],
        memberships: membershipsByUserId[profile.userId] || [],
        now,
      });
      const risk = scoreClientRetentionRisk(signals);
      return {
        profile,
        signals,
        risk,
      };
    })
    .filter((entry) => entry.risk.score > 0)
    .sort((left, right) => right.risk.score - left.risk.score);
}

export function suggestNextServiceForClient(
  clientProfile = {},
  {
    bookings = [],
    services = [],
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const relevantBookings = (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .filter((booking) => booking.requesterUserId === normalizedProfile.userId)
    .sort((left, right) => toTimestampValue(right.startAt || right.updatedAt) - toTimestampValue(left.startAt || left.updatedAt));
  const lastBooking = relevantBookings[0] || null;

  if (lastBooking?.serviceId) {
    return (services || []).find((service) => (service.serviceId || service.id) === lastBooking.serviceId) || null;
  }

  if (lastBooking?.discipline) {
    return (services || []).find((service) => service.active !== false && service.discipline === lastBooking.discipline) || null;
  }

  if (normalizedProfile.primaryDiscipline) {
    return (services || []).find((service) => service.active !== false && service.discipline === normalizedProfile.primaryDiscipline) || null;
  }

  // Pass 80 §13 — NO arbitrary first-active-service fallback: an unsupported service is null (operator selects).
  return null;
}

export function suggestPackageForClient(
  clientProfile = {},
  {
    memberships = [],
    servicePackages = [],
    bookings = [],
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const activeMemberships = (memberships || [])
    .map((membership) => normalizeCustomerMembership(membership))
    .filter((membership) => membership.userId === normalizedProfile.userId && membership.status === 'active');

  if (activeMemberships.length > 0) return null;

  const recentBooking = (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .filter((booking) => booking.requesterUserId === normalizedProfile.userId)
    .sort((left, right) => toTimestampValue(right.startAt || right.updatedAt) - toTimestampValue(left.startAt || left.updatedAt))[0];

  return (servicePackages || []).find((servicePackage) => {
    if (servicePackage.active === false) return false;
    // Pass 80 §15 — no arbitrary first-active-package fallback: with no booking evidence there is no package suggestion.
    if (!recentBooking) return false;
    const disciplines = servicePackage.applicableDisciplines || [];
    const serviceTypes = servicePackage.applicableServiceTypes || [];
    const disciplineMatch = disciplines.length === 0 || disciplines.includes(recentBooking.discipline);
    const serviceTypeMatch = serviceTypes.length === 0 || serviceTypes.includes(recentBooking.serviceType);
    return disciplineMatch && serviceTypeMatch;
  }) || null;
}

export function suggestRebookTiming(
  clientProfile = {},
  {
    bookings = [],
    now = new Date(),
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const recentCompleted = (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .filter((booking) => booking.requesterUserId === normalizedProfile.userId && booking.status === 'completed')
    .sort((left, right) => toTimestampValue(right.endAt || right.updatedAt) - toTimestampValue(left.endAt || left.updatedAt))[0];

  if (!recentCompleted) {
    return {
      timing: 'whenever_ready',
      message: 'No completed session history yet. Rebook timing can stay flexible.',
    };
  }

  const daysSince = Math.floor((now.getTime() - toTimestampValue(recentCompleted.endAt || recentCompleted.updatedAt)) / (24 * 60 * 60 * 1000));
  if (daysSince >= 21) {
    return {
      timing: 'now',
      message: 'This client is due for a rebook now based on the last completed session.',
    };
  }
  if (daysSince >= 10) {
    return {
      timing: 'soon',
      message: 'This client is approaching a good rebook window.',
    };
  }

  return {
    timing: 'later',
    message: 'This client was seen recently, so a follow-up can wait a bit.',
  };
}

export function suggestFollowUpMessage(
  clientProfile = {},
  {
    recommendedService = null,
    packageOpportunity = null,
    rebookTiming = null,
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const serviceLabel = recommendedService?.name || 'your next session';
  if (packageOpportunity?.name) {
    return `Hi ${normalizedProfile.displayName}, you still have momentum from your recent coaching. ${packageOpportunity.name} may be a strong fit if you want to keep progressing consistently.`;
  }

  if (rebookTiming?.timing === 'now') {
    return `Hi ${normalizedProfile.displayName}, your coach recommends booking ${serviceLabel} soon so your progress stays on track.`;
  }

  return `Hi ${normalizedProfile.displayName}, when you're ready, ${serviceLabel} would be a strong next step.`;
}

export function suggestNextCoachingFocus(
  clientProfile = {},
  {
    bookings = [],
    feedbackHistory = [],
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const recentBooking = (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .filter((booking) => booking.requesterUserId === normalizedProfile.userId)
    .sort((left, right) => toTimestampValue(right.startAt || right.updatedAt) - toTimestampValue(left.startAt || left.updatedAt))[0];
  const recentFeedback = (feedbackHistory || [])[0] || null;

  if (recentFeedback?.hasAnalysis) {
    return 'Recent human movement analysis is available, so the next session can reinforce the latest coaching themes.';
  }

  if (recentBooking?.discipline || normalizedProfile.primaryDiscipline) {
    return `Continue building on ${recentBooking?.discipline || normalizedProfile.primaryDiscipline} fundamentals with a focused follow-up session.`;
  }

  return 'Focus on the next coaching priority from the most recent lesson or booking.';
}

export function buildClientNextStepRecommendations(
  clientProfile = {},
  {
    bookings = [],
    services = [],
    memberships = [],
    servicePackages = [],
    feedbackHistory = [],
    now = new Date(),
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const nextService = suggestNextServiceForClient(normalizedProfile, { bookings, services });
  const packageSuggestion = suggestPackageForClient(normalizedProfile, {
    memberships,
    servicePackages,
    bookings,
  });
  const rebookTiming = suggestRebookTiming(normalizedProfile, { bookings, now });
  const coachingFocus = suggestNextCoachingFocus(normalizedProfile, {
    bookings,
    feedbackHistory,
  });
  const followUpMessage = suggestFollowUpMessage(normalizedProfile, {
    recommendedService: nextService,
    packageOpportunity: packageSuggestion,
    rebookTiming,
  });

  return {
    nextService,
    packageSuggestion,
    rebookTiming,
    coachingFocus,
    followUpMessage,
  };
}

export function buildRebookSuggestionExplanation(
  clientProfile = {},
  {
    recommendations = {},
    risk = null,
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const reasons = risk?.reasons?.length ? risk.reasons.join(' ') : recommendations?.rebookTiming?.message || 'The recent coaching cadence suggests a follow-up would help.';
  return `${normalizedProfile.displayName} is being surfaced for rebooking because ${reasons}`;
}

export function buildClientNextStepExplanation(
  clientProfile = {},
  {
    recommendations = {},
    risk = null,
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const nextServiceName = recommendations?.nextService?.name || 'a follow-up service';
  const packageName = recommendations?.packageSuggestion?.name || '';
  const riskSummary = risk?.reasons?.[0] || recommendations?.rebookTiming?.message || 'the recent activity pattern suggests a timely follow-up.';
  return `${normalizedProfile.displayName} is a fit for ${nextServiceName} because ${riskSummary}${packageName ? ` ${packageName} is also a relevant package option.` : ''}`;
}

export function buildFollowUpDraftPrompt(
  clientProfile = {},
  {
    recommendations = {},
    risk = null,
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const explanation = buildClientNextStepExplanation(normalizedProfile, {
    recommendations,
    risk,
  });
  return `Draft a short, friendly follow-up message for ${normalizedProfile.displayName}. Keep it human, specific, and non-pushy. Context: ${explanation}`;
}

export function buildPackageOfferDraftPrompt(
  clientProfile = {},
  {
    recommendations = {},
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const packageName = recommendations?.packageSuggestion?.name || 'a suitable package';
  const nextServiceName = recommendations?.nextService?.name || 'their next session';
  return `Draft a concise package-offer message for ${normalizedProfile.displayName}. Explain why ${packageName} could support continued progress and connect it to ${nextServiceName}.`;
}

export function buildCoachSummaryForClient(
  workspace = {},
  {
    risk = null,
  } = {}
) {
  const clientProfile = normalizeClientProfile(workspace?.clientProfile || {});
  const lastActivity = workspace?.lastActivity?.at ? new Date(workspace.lastActivity.at).toLocaleDateString() : 'no recent activity';
  const recommendations = workspace?.recommendations || {};
  return [
    `${clientProfile.displayName} last showed activity on ${lastActivity}.`,
    recommendations?.coachingFocus || 'No coaching focus suggestion yet.',
    risk?.reasons?.[0] || recommendations?.rebookTiming?.message || 'No retention risk explanation available yet.',
  ].join(' ');
}

export function buildClientProgressSummary(workspace = {}) {
  const clientProfile = normalizeClientProfile(workspace?.clientProfile || {});
  const lastLesson = workspace?.lessons?.[0];
  const lastFeedback = workspace?.feedbackHistory?.[0];
  const recentService = workspace?.bookings?.[0]?.serviceNameSnapshot || workspace?.recommendations?.nextService?.name || 'recent coaching';

  return [
    `${clientProfile.displayName} has ${workspace?.bookings?.length || 0} confirmed sessions on record.`,
    recentService ? `Recent service context: ${recentService}.` : '',
    lastLesson ? `Most recent lesson activity was updated on ${toDateValue(lastLesson.updatedAt || lastLesson.date || lastLesson.createdAt)?.toLocaleDateString?.() || 'a recent date'}.` : '',
    lastFeedback ? 'Human coaching review is available from the latest session.' : 'No recent review artifact is linked yet.',
    clientProfile.goals ? `Goals currently noted: ${clientProfile.goals}.` : '',
  ].filter(Boolean).join(' ');
}

export function buildNextFocusSummary(workspace = {}) {
  const recommendations = workspace?.recommendations || {};
  const nextService = recommendations?.nextService?.name || 'a follow-up service';
  return `${recommendations?.coachingFocus || 'Keep reinforcing recent coaching priorities.'} ${nextService ? `The next likely service is ${nextService}.` : ''}`.trim();
}

export function buildRebookCoachingSummary(workspace = {}) {
  const recommendations = workspace?.recommendations || {};
  return [
    buildClientProgressSummary(workspace),
    recommendations?.rebookTiming?.message || 'A rebook can be timed around the most recent coaching cadence.',
  ].filter(Boolean).join(' ');
}

export function buildPackageOfferCoachingSummary(workspace = {}) {
  const recommendations = workspace?.recommendations || {};
  return [
    buildClientProgressSummary(workspace),
    recommendations?.packageSuggestion?.name
      ? `${recommendations.packageSuggestion.name} appears relevant based on the recent coaching pattern.`
      : 'No package recommendation is currently strong enough to highlight.',
  ].filter(Boolean).join(' ');
}

export function createPackageOfferSuggestion(
  clientProfile = {},
  {
    packageSuggestion = null,
    recommendedService = null,
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  if (!packageSuggestion) return null;
  return {
    clientUserId: normalizedProfile.userId,
    packageId: packageSuggestion.servicePackageId || packageSuggestion.id || '',
    packageName: packageSuggestion.name || 'Package',
    recommendedServiceId: recommendedService?.serviceId || recommendedService?.id || '',
    summary: `${packageSuggestion.name || 'This package'} could support ${normalizedProfile.displayName}'s next steps.`,
  };
}

export function createRebookAction(
  clientProfile = {},
  {
    recommendations = {},
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  return {
    clientUserId: normalizedProfile.userId,
    serviceId: recommendations?.nextService?.serviceId || recommendations?.nextService?.id || '',
    serviceName: recommendations?.nextService?.name || '',
    timing: recommendations?.rebookTiming?.timing || 'soon',
    summary: recommendations?.rebookTiming?.message || `Create the next booking request for ${normalizedProfile.displayName}.`,
  };
}

export async function markRetentionItemReviewed(
  db,
  {
    organizationId = '',
    userId = '',
    reviewedByUserId = '',
    queueType = '',
  } = {}
) {
  return updateClientProfile(db, {
    organizationId,
    userId,
    updates: {
      retentionLastReviewedAt: new Date().toISOString(),
      retentionLastReviewedByUserId: reviewedByUserId,
      retentionLastActionType: 'reviewed',
      retentionLastQueueType: queueType,
    },
  });
}

export async function snoozeRetentionRecommendation(
  db,
  {
    organizationId = '',
    userId = '',
    snoozedByUserId = '',
    queueType = '',
    until = null,
  } = {}
) {
  const snoozedUntil = until || new Date(Date.now() + (7 * 24 * 60 * 60 * 1000)).toISOString();
  return updateClientProfile(db, {
    organizationId,
    userId,
    updates: {
      retentionSnoozedUntil: snoozedUntil,
      retentionSnoozedByUserId: snoozedByUserId,
      retentionLastActionType: 'snoozed',
      retentionLastQueueType: queueType,
    },
  });
}

function isRetentionSnoozed(profile = {}, now = new Date()) {
  const snoozedUntil = toTimestampValue(profile.retentionSnoozedUntil);
  return snoozedUntil > now.getTime();
}

function normalizeQueueResult(clientProfiles = []) {
  return (clientProfiles || []).map((profile) => normalizeClientProfile(profile));
}

export function buildOrganizationRetentionQueues(
  {
    clientProfiles = [],
    bookings = [],
    memberships = [],
    timelineItemsByUserId = {},
    now = new Date(),
  } = {}
) {
  const eligibleProfiles = normalizeQueueResult(clientProfiles).filter((profile) => !isRetentionSnoozed(profile, now));
  return {
    needingRebook: normalizeQueueResult(getClientsNeedingRebook(eligibleProfiles, { bookings, now })),
    unusedCredits: normalizeQueueResult(getClientsWithUnusedCredits(memberships).map((membership) => eligibleProfiles.find((profile) => normalizeClientProfile(profile).userId === membership.userId)).filter(Boolean)),
    expiringMemberships: normalizeQueueResult(getClientsWithExpiringMemberships(memberships, { now }).map((membership) => eligibleProfiles.find((profile) => normalizeClientProfile(profile).userId === membership.userId)).filter(Boolean)),
    inactiveClients: normalizeQueueResult(getInactiveClients(eligibleProfiles, { timelineItemsByUserId, now })),
    feedbackReadyNoBooking: normalizeQueueResult(getClientsWithFeedbackReadyNoBooking(eligibleProfiles, { bookings })),
    followUpRecommended: normalizeQueueResult(getClientsWithFollowUpRecommended(eligibleProfiles, { bookings })),
    packageBalanceNoUpcoming: normalizeQueueResult(getClientsWithPackageBalanceNoUpcomingSession(eligibleProfiles, { bookings, memberships })),
  };
}

export function buildInstructorRetentionQueues(
  {
    clientProfiles = [],
    bookings = [],
    memberships = [],
    timelineItemsByUserId = {},
    instructorId = '',
    now = new Date(),
  } = {}
) {
  const instructorBookings = (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .filter((booking) => !instructorId || booking.instructorId === instructorId);
  const userIds = new Set(instructorBookings.map((booking) => booking.requesterUserId).filter(Boolean));
  const instructorProfiles = (clientProfiles || []).map((profile) => normalizeClientProfile(profile)).filter((profile) => userIds.has(profile.userId));
  const instructorMemberships = (memberships || []).map((membership) => normalizeCustomerMembership(membership)).filter((membership) => userIds.has(membership.userId));
  const filteredTimeline = Object.fromEntries(
    Object.entries(timelineItemsByUserId || {}).filter(([userId]) => userIds.has(userId))
  );

  return buildOrganizationRetentionQueues({
    clientProfiles: instructorProfiles,
    bookings: instructorBookings,
    memberships: instructorMemberships,
    timelineItemsByUserId: filteredTimeline,
    now,
  });
}

export function getServiceRebookRate(service = {}, bookings = []) {
  const serviceId = service.serviceId || service.id || '';
  const relevant = (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .filter((booking) => !serviceId || booking.serviceId === serviceId);

  if (relevant.length === 0) {
    return { serviceId, totalBookings: 0, repeatBookings: 0, rebookRate: 0 };
  }

  const countsByUserId = relevant.reduce((acc, booking) => {
    const userId = booking.requesterUserId || '';
    if (!userId) return acc;
    acc[userId] = (acc[userId] || 0) + 1;
    return acc;
  }, {});
  const repeatBookings = Object.values(countsByUserId).filter((count) => count > 1).length;

  return {
    serviceId,
    totalBookings: relevant.length,
    repeatBookings,
    rebookRate: Number((repeatBookings / Math.max(Object.keys(countsByUserId).length, 1)).toFixed(2)),
  };
}

export function getPackageConversionByService(service = {}, bookings = []) {
  const serviceId = service.serviceId || service.id || '';
  const relevant = (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .filter((booking) => !serviceId || booking.serviceId === serviceId);
  const covered = relevant.filter((booking) => (booking.packageCoverageAmount || 0) > 0 || booking.packageId);

  return {
    serviceId,
    totalBookings: relevant.length,
    coveredBookings: covered.length,
    packageConversionRate: relevant.length > 0 ? Number((covered.length / relevant.length).toFixed(2)) : 0,
  };
}

export function getServiceRetentionSummary(services = [], bookings = []) {
  return (services || []).map((service) => ({
    serviceId: service.serviceId || service.id || '',
    serviceName: service.name || 'Service',
    ...getServiceRebookRate(service, bookings),
    ...getPackageConversionByService(service, bookings),
  }));
}

export function getInstructorRetentionSummary(
  clientProfiles = [],
  {
    bookings = [],
    instructorId = '',
  } = {}
) {
  const relevantBookings = (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .filter((booking) => !instructorId || booking.instructorId === instructorId);
  const uniqueClients = new Set(relevantBookings.map((booking) => booking.requesterUserId).filter(Boolean));
  const clientsNeedingRebook = getClientsNeedingRebook(clientProfiles, { bookings: relevantBookings });

  return {
    clientCount: uniqueClients.size,
    confirmedBookingCount: relevantBookings.filter((booking) => ['confirmed', 'upcoming', 'in_progress'].includes(booking.status)).length,
    completedBookingCount: relevantBookings.filter((booking) => booking.status === 'completed').length,
    clientsNeedingRebook: clientsNeedingRebook.length,
  };
}

export function getRebookConversionRate(bookings = []) {
  const normalizedBookings = (bookings || []).map((booking) => normalizeBooking(booking));
  const rebookRequests = normalizedBookings.filter((booking) => booking.sourceType === 'client_profile_rebook');
  const converted = rebookRequests.filter((booking) => ['confirmed', 'upcoming', 'in_progress', 'completed'].includes(booking.status));
  return {
    totalRebookRequests: rebookRequests.length,
    convertedBookings: converted.length,
    rebookConversionRate: rebookRequests.length > 0 ? Number((converted.length / rebookRequests.length).toFixed(2)) : 0,
  };
}

// Pass 80 §45 — `booking.status === 'completed'` is a COMPLETED BOOKING count, NOT a delivered lesson. When
// `lessons` are supplied, a truthful `deliveredLessonCount` is computed from delivery-bearing lessonInstances
// (Pass-79B canonical delivery truth); the booking-status number is exposed only as `completedBookingCount`.
export function getServiceConversionFunnel(services = [], { bookings = [], bookingRequests = [], lessons = [] } = {}) {
  return (services || []).map((service) => {
    const serviceId = service.serviceId || service.id || '';
    const relatedRequests = (bookingRequests || []).filter((bookingRequest) => (bookingRequest.serviceId || '') === serviceId);
    const relatedBookings = (bookings || []).map((booking) => normalizeBooking(booking)).filter((booking) => booking.serviceId === serviceId);
    const relatedBookingIds = new Set(relatedBookings.map((b) => b.bookingId || b.id).filter(Boolean));
    const completedBookings = relatedBookings.filter((booking) => booking.status === 'completed').length;
    const deliveredLessons = (lessons || []).filter((l) => isDeliveryBearing(l) && (String(l.serviceId || '') === serviceId || relatedBookingIds.has(String(l.bookingId || '')))).length;
    return {
      serviceId,
      serviceName: service.name || 'Service',
      bookingRequestCount: relatedRequests.length,
      confirmedBookingCount: relatedBookings.length,
      completedBookingCount: completedBookings,
      deliveredLessonCount: deliveredLessons,
      ...getServiceRebookRate(service, relatedBookings),
      ...getPackageConversionByService(service, relatedBookings),
    };
  });
}

export function getInstructorConversionFunnel(instructorId = '', { bookings = [], bookingRequests = [] } = {}) {
  const relatedRequests = (bookingRequests || []).filter((bookingRequest) => !instructorId || bookingRequest.instructorId === instructorId);
  const relatedBookings = (bookings || []).map((booking) => normalizeBooking(booking)).filter((booking) => !instructorId || booking.instructorId === instructorId);
  return {
    instructorId,
    bookingRequestCount: relatedRequests.length,
    confirmedBookingCount: relatedBookings.length,
    completedBookingCount: relatedBookings.filter((booking) => booking.status === 'completed').length,
    ...getRebookConversionRate(relatedBookings),
  };
}

export function getFollowUpEffectivenessSummary(clientProfiles = [], { bookings = [] } = {}) {
  const normalizedBookings = (bookings || []).map((booking) => normalizeBooking(booking));
  const profilesById = (clientProfiles || []).reduce((acc, profile) => {
    const normalizedProfile = normalizeClientProfile(profile);
    acc[normalizedProfile.userId] = normalizedProfile;
    return acc;
  }, {});
  const reviewedProfiles = Object.values(profilesById).filter((profile) => Boolean(profile.retentionLastReviewedAt));
  const rebookedAfterReview = reviewedProfiles.filter((profile) =>
    normalizedBookings.some((booking) => booking.requesterUserId === profile.userId && booking.sourceType === 'client_profile_rebook')
  );
  return {
    reviewedClientCount: reviewedProfiles.length,
    rebookedAfterReviewCount: rebookedAfterReview.length,
    reviewedToRebookRate: reviewedProfiles.length > 0 ? Number((rebookedAfterReview.length / reviewedProfiles.length).toFixed(2)) : 0,
  };
}

// Pass 80 §34 — a rebook request is considered still-open (so a second click is a no-op) unless it has already
// been actioned. These are the terminal states after which a NEW rebook cycle is legitimate.
const CLOSED_REBOOK_REQUEST_STATUSES = new Set(['converted', 'booked', 'cancelled', 'declined', 'closed', 'expired', 'archived']);

export async function createRebookRequestFromLastService(
  db,
  {
    organizationId = '',
    clientProfile = {},
    clientId = '',
    bookings = [],
    bookingRequests = [],
    services = [],
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const suggestedService = suggestNextServiceForClient(normalizedProfile, {
    bookings,
    services,
  });
  // Pass 70A — a genuine commercial-only client (clientId, no Auth) can rebook: match the last service by the
  // canonical clientId (modern) or requesterUserId (legacy). requesterUserId is NEVER fabricated from clientId.
  const matchesClient = (b) => (clientId && b.clientId === clientId) || (normalizedProfile.userId && b.requesterUserId === normalizedProfile.userId);

  // §34 duplicate-safe: if an operator-created rebook request for this client is still open, return it instead of
  // minting a second one. Identity match is clientId (modern) or requesterUserId (legacy) — never fabricated.
  const openRebookRequest = (bookingRequests || [])
    .filter((request) => request && request.sourceType === 'client_profile_rebook' && matchesClient(request))
    .filter((request) => !CLOSED_REBOOK_REQUEST_STATUSES.has(String(request.status || '').toLowerCase()))
    .sort((left, right) => toTimestampValue(right.createdAt || right.updatedAt) - toTimestampValue(left.createdAt || left.updatedAt))[0];
  if (openRebookRequest) {
    return { ...openRebookRequest, alreadyExisted: true };
  }

  const lastBooking = (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .filter(matchesClient)
    .sort((left, right) => toTimestampValue(right.startAt || right.updatedAt) - toTimestampValue(left.startAt || left.updatedAt))[0] || {};

  return createBookingRequest(db, {
    organizationId: organizationId || normalizedProfile.organizationId || lastBooking.organizationId || '',
    instructorId: lastBooking.instructorId || suggestedService?.instructorId || '',
    // NEW commercial cycle: a fresh request identity (createBookingRequest mints a new id); the old
    // bookingId/requestSlotId/lessonInstanceId are NEVER reused. clientId is preserved; requesterUserId
    // stays whatever the profile actually had (empty for a no-Auth customer — no fabrication).
    clientId: clientId || '',
    requesterUserId: normalizedProfile.userId,
    requesterName: normalizedProfile.displayName,
    requesterEmail: normalizedProfile.email,
    requesterPhone: normalizedProfile.phone,
    serviceId: suggestedService?.serviceId || suggestedService?.id || lastBooking.serviceId || '',
    serviceNameSnapshot: suggestedService?.name || lastBooking.serviceNameSnapshot || '',
    serviceDurationSnapshot: suggestedService?.durationMinutes ?? lastBooking.serviceDurationSnapshot ?? null,
    servicePricingSnapshot: suggestedService?.price ?? lastBooking.servicePricingSnapshot ?? null,
    servicePackageEligible: Boolean(suggestedService?.packageEligible ?? lastBooking.servicePackageEligible),
    applicablePackageIds: suggestedService?.applicablePackageIds || lastBooking.applicablePackageIds || [],
    discipline: suggestedService?.discipline || lastBooking.discipline || normalizedProfile.primaryDiscipline || '',
    serviceType: suggestedService?.serviceType || lastBooking.serviceType || '',
    timezone: lastBooking.timezone || 'UTC',
    sourceType: 'client_profile_rebook',
    notes: `Rebook request created from client CRM for ${normalizedProfile.displayName}.`,
    actorUserId,
    actorName,
  });
}

export async function sendRebookPromptNotification(
  db,
  {
    clientProfile = {},
    booking = {},
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  if (!normalizedProfile.userId) return null;

  if (booking && (booking.bookingId || booking.id)) {
    return sendRebookPrompt(db, {
      booking,
      actorUserId,
      actorName,
    });
  }

  return createNotification(db, {
    userId: normalizedProfile.userId,
    organizationId: normalizedProfile.organizationId,
    audience: 'commercial', // Pass 85.1B.3 — client rebook nudge (commercial), fail-closed for the staff branch
    type: 'booking_rebook_recommended',
    title: 'Time to book your next session',
    message: `Your coaching team at ${normalizedProfile.organizationId || 'the organization'} recommends scheduling the next session soon.`,
    link: '/my-bookings',
    actorUserId,
    actorName,
    relatedEntityType: 'client_profile',
    relatedEntityId: normalizedProfile.clientProfileId || normalizedProfile.userId,
  });
}

// Pass 79 — building a client workspace is READ-ONLY (§21/§44): opening/viewing a client NEVER writes a profile
// cache. It is clientId-native: a commercial customer with NO Auth account resolves fully via the canonical
// `clientId`. Profile persistence happens ONLY on an explicit Save action (upsertClientProfile). `persistProfile` is
// retained for backward-compatibility but defaults to false and is ignored — no view ever persists.
export async function buildClientWorkspace(
  db,
  {
    organizationId = '',
    clientUserId = '',
    clientId = '',
    persistProfile = false, // eslint-disable-line no-unused-vars
    services = [],
  } = {}
) {
  // Lessons first — their explicit `studentId`s are the ONLY authorized participants whose reviewed video evidence
  // may enter this client's coaching memory (the purchaser is never assumed to be the coached participant).
  const [userRecord, bookingHistory, lessons, packageSummary, payments, clientNotes, savedProfile] = await Promise.all([
    clientUserId ? loadUserRecord(db, clientUserId) : Promise.resolve(null),
    getClientBookingHistory(db, { organizationId, clientUserId, clientId }), // Pass 70A — modern clientId bookings
    getClientLessonHistory(db, { organizationId, clientUserId, clientId }), // Pass 69 — modern clientId lessons
    getClientPackageSummary(db, { organizationId, clientUserId }),
    getRequesterBookingPayments(db, clientUserId),
    getClientNotes(db, { organizationId, clientId, clientUserId }),
    getClientProfile(db, { organizationId, clientId, userId: clientUserId }),
  ]);

  const deliveredLessons = (lessons || []).filter((l) => isDeliveryBearing(l));
  const participantIds = [...new Set(deliveredLessons.map((l) => String(l.studentId || '').trim()).filter(Boolean))];
  // Pass 79 REPAIR §3 — participant-LINKED video evidence only. On the canonical clientId path the commercial account
  // holder / purchaser (clientUserId) is NEVER auto-loaded; only explicit coached participants contribute.
  const videos = await getClientVideosForOwners(db, {
    organizationId,
    ownerIds: resolveParticipantVideoOwnerIds({ clientId, clientUserId, participantIds }),
  });

  const feedbackHistory = getClientFeedbackHistory(videos, bookingHistory.bookings);
  const timeline = await getClientTimeline(db, {
    organizationId,
    clientUserId,
    clientId,
    bookingHistory,
    lessons,
    videos,
    notes: clientNotes,
    packageSummary,
  });
  const lastMessageActivity = timeline.find((entry) => entry.type === 'message');
  const revenueSummary = getClientRevenueSummary(bookingHistory.bookings, payments);

  // §22 commercial display snapshot — from the Auth `users` record when present, else the most recent canonical
  // commercial record carrying the SAME clientId (booking → request). Never fuzzy-matched to another customer.
  const commercialSnapshot = bookingHistory.bookings[0] || bookingHistory.bookingRequests[0] || {};
  const derivedProfile = normalizeClientProfile({
    organizationId,
    clientId,
    userId: clientUserId,
    firstName: userRecord?.firstName || '',
    lastName: userRecord?.lastName || '',
    displayName: userRecord?.name || buildDisplayName(userRecord || {}) || commercialSnapshot.requesterName || commercialSnapshot.clientName || '',
    email: userRecord?.email || commercialSnapshot.requesterEmail || '',
    phone: userRecord?.phone || commercialSnapshot.requesterPhone || '',
    country: userRecord?.country || '',
    primaryDiscipline: bookingHistory.bookings[0]?.discipline || bookingHistory.bookingRequests[0]?.discipline || '',
    skillLevel: userRecord?.skillLevel || '',
    goals: userRecord?.goals || '',
    lastLessonAt: deliveredLessons.length ? lessonHistoryTime(deliveredLessons[0]) : null,
    lastBookingAt: bookingHistory.bookings[0]?.startAt || bookingHistory.bookingRequests[0]?.requestedStartAt || null,
    lastMessageAt: lastMessageActivity?.at || null,
    totalBookings: bookingHistory.bookings.length,
    totalLessons: deliveredLessons.length,
    totalVideos: videos.length,
    totalRevenue: revenueSummary.totalRevenue,
  });

  // §35 — DERIVED canonical facts (counts/last-*/contact) fill in; EXPLICIT operator-saved CRM fields override and
  // are NEVER clobbered by a stale derived snapshot. No write happens here.
  const clientProfile = savedProfile
    ? normalizeClientProfile({
        ...derivedProfile,
        clientProfileId: savedProfile.clientProfileId,
        id: savedProfile.clientProfileId,
        displayName: savedProfile.displayName || derivedProfile.displayName,
        primaryDiscipline: savedProfile.primaryDiscipline || derivedProfile.primaryDiscipline,
        skillLevel: savedProfile.skillLevel || derivedProfile.skillLevel,
        goals: savedProfile.goals || derivedProfile.goals,
        tags: savedProfile.tags?.length ? savedProfile.tags : derivedProfile.tags,
        notesInternal: savedProfile.notesInternal || derivedProfile.notesInternal,
      }, savedProfile.clientProfileId)
    : derivedProfile;

  const recommendations = buildClientNextStepRecommendations(clientProfile, {
    bookings: bookingHistory.bookings,
    services,
    memberships: packageSummary.activeMemberships,
    servicePackages: packageSummary.servicePackages,
    feedbackHistory,
  });

  // §4 the ONE derived longitudinal coaching memory — participant-safe, sport-safe, human-authoritative.
  const coachingMemory = buildClientCoachingMemory({
    clientId,
    organizationId,
    bookings: bookingHistory.bookings,
    bookingRequests: bookingHistory.bookingRequests,
    lessons,
    videos,
    notes: clientNotes,
    clientGoals: clientProfile.goals,
  });

  return {
    clientProfile,
    hasAuthAccount: Boolean(clientUserId), // §26/§37/§50 — distinguishes "no account" from a real zero
    clientId,
    clientUserId,
    userRecord,
    bookingRequests: bookingHistory.bookingRequests,
    bookings: bookingHistory.bookings,
    lessons,
    videos,
    feedbackHistory,
    packageSummary,
    revenueSummary,
    notes: clientNotes,
    timeline,
    coachingMemory,
    lastActivity: getClientLastActivity(timeline),
    recommendations,
  };
}
